import { api } from "@/lib/api/client";

export type BillingSku = {
  id: number;
  skuKey: string;
  name: string;
  description: string;
  priceClp: number;
  frequency: "one_shot" | "monthly" | "yearly" | "per_event";
  featureKeys: string[];
  isBundle: boolean;
  webhookPrefix: string | null;
  isActive: boolean;
  entitlementDurationDays: number | null;
  ctaLabel: string | null;
};

export type Entitlement = {
  id: number;
  subjectType: string;
  subjectId: number;
  featureKey: string;
  status: string;
  source: string;
  skuKey: string | null;
  expiresAt: string | null;
  scopeEventId: number | null;
  createdAt: string;
};

export type BillingSettings = {
  // Comisión Mixcore sobre la venta de consumos (#23), como decimal: 0.04 = 4%.
  consumoCommissionRate: number;
};

export type PromoCodeType = "free" | "percent";

export type PromoCode = {
  id: number;
  code: string;
  type: PromoCodeType;
  skuKey: string | null;
  durationDays: number | null;
  percentOff: number | null;
  maxRedemptions: number | null;
  perSubjectLimit: number;
  currentUses: number;
  validUntil: string | null;
  isActive: boolean;
  note: string | null;
  createdByUserId: number | null;
  createdAt: string;
  updatedAt: string;
};

export type PromoCodeRedemption = {
  id: number;
  promoCodeId: number;
  code: string;
  type: PromoCodeType;
  subjectType: string;
  subjectId: number;
  redeemedByUserId: number | null;
  skuKey: string | null;
  featureKeysGranted: string[];
  entitlementIds: number[] | null;
  discountPercent: number | null;
  redeemedAt: string;
};

export type CreatePromoCodeBody = {
  code: string;
  type: PromoCodeType;
  skuKey?: string | null;
  durationDays?: number | null;
  percentOff?: number | null;
  maxRedemptions?: number | null;
  perSubjectLimit?: number;
  validUntil?: string | null;
  note?: string | null;
};

export const billingApi = {
  getSettings: async (): Promise<BillingSettings> => {
    const { data } = await api.get<BillingSettings>("/api/nexus/billing/settings");
    return data;
  },
  updateSettings: async (body: BillingSettings): Promise<BillingSettings> => {
    const { data } = await api.put<BillingSettings>("/api/nexus/billing/settings", body);
    return data;
  },
  listSkus: async (): Promise<BillingSku[]> => {
    const { data } = await api.get<BillingSku[]>("/api/nexus/billing/skus");
    return data;
  },
  updateSku: async (id: number, body: Partial<BillingSku>): Promise<BillingSku> => {
    const { data } = await api.put<BillingSku>(`/api/nexus/billing/skus/${id}`, body);
    return data;
  },
  createSku: async (body: Partial<BillingSku>): Promise<BillingSku> => {
    const { data } = await api.post<BillingSku>("/api/nexus/billing/skus", body);
    return data;
  },
  listEntitlements: async (params: { status?: string }): Promise<Entitlement[]> => {
    const { data } = await api.get<Entitlement[]>("/api/nexus/billing/entitlements", { params });
    return data;
  },
  grant: async (body: {
    subjectType: string;
    subjectId: number;
    featureKey: string;
    durationDays?: number;
    note?: string;
  }): Promise<Entitlement> => {
    const { data } = await api.post<Entitlement>("/api/nexus/billing/entitlements/grant", body);
    return data;
  },
  revoke: async (id: number): Promise<void> => {
    await api.post(`/api/nexus/billing/entitlements/${id}/revoke`);
  },

  // ── Códigos promocionales ──────────────────────────────────────
  listPromoCodes: async (params?: { isActive?: string; type?: string }): Promise<PromoCode[]> => {
    const { data } = await api.get<PromoCode[]>("/api/nexus/billing/promo-codes", { params });
    return data;
  },
  createPromoCode: async (body: CreatePromoCodeBody): Promise<PromoCode> => {
    const { data } = await api.post<PromoCode>("/api/nexus/billing/promo-codes", body);
    return data;
  },
  updatePromoCode: async (id: number, body: Partial<CreatePromoCodeBody> & { isActive?: boolean }): Promise<PromoCode> => {
    const { data } = await api.put<PromoCode>(`/api/nexus/billing/promo-codes/${id}`, body);
    return data;
  },
  deactivatePromoCode: async (id: number): Promise<void> => {
    await api.post(`/api/nexus/billing/promo-codes/${id}/deactivate`);
  },
  listRedemptions: async (id: number): Promise<PromoCodeRedemption[]> => {
    const { data } = await api.get<PromoCodeRedemption[]>(`/api/nexus/billing/promo-codes/${id}/redemptions`);
    return data;
  },
};
